import { countryLabel } from "./format";
import { countryName } from "./countries";

/** Developed markets - useful to test ask / currency switch outside the shop’s Markets. */
export const PREVIEW_DEVELOPED = [
  "US",
  "CA",
  "GB",
  "DE",
  "FR",
  "AU",
  "JP",
  "NL",
  "SE",
  "SG",
  "KR",
  "CH",
] as const;

/**
 * Large-population / emerging - usually out-of-market → Stay (no ask).
 * Good contrast tests vs Markets countries.
 */
export const PREVIEW_POPULOUS = [
  "IN",
  "ID",
  "NG",
  "BR",
  "PK",
  "BD",
  "PH",
  "VN",
  "EG",
  "MX",
  "ET",
  "ZA",
] as const;

export type PreviewCountryGroup = "market" | "developed" | "populous";

export type PreviewCountryOption = {
  code: string;
  name: string;
  group: PreviewCountryGroup;
  /** Primary market (home) - preview usually should not ask. */
  primary?: boolean;
};

function uniqCodes(codes: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of codes) {
    const c = String(raw || "")
      .toUpperCase()
      .slice(0, 2);
    if (!/^[A-Z]{2}$/.test(c) || seen.has(c)) continue;
    seen.add(c);
    out.push(c);
  }
  return out;
}

/** Curated picker list: shop Markets first, then developed, then populous. */
export function buildPreviewCountryOptions(
  marketCountries: string[] | undefined | null,
): PreviewCountryOption[] {
  const markets = uniqCodes(marketCountries || []);
  const home = markets[0] || "";
  const marketSet = new Set(markets);
  const out: PreviewCountryOption[] = markets.map((code) => ({
    code,
    name: countryName(code),
    group: "market",
    primary: code === home,
  }));
  for (const code of PREVIEW_DEVELOPED) {
    if (marketSet.has(code)) continue;
    out.push({ code, name: countryName(code), group: "developed" });
  }
  for (const code of PREVIEW_POPULOUS) {
    if (marketSet.has(code)) continue;
    out.push({ code, name: countryName(code), group: "populous" });
  }
  return out;
}

/** Pick a visitor country that is not the shop’s home market (so a popup can show). */
export function pickVisitorCountry(
  marketCountries: string[] | undefined | null,
  fallback = "CA",
): string {
  const list = uniqCodes(marketCountries || []);
  const home = list[0];
  if (list.length > 1) {
    const other = list.find((c) => c !== home);
    if (other) return other;
  }
  if (home && home !== fallback) return fallback;
  if (home === "CA") return "GB";
  if (home === "US") return "CA";
  return fallback;
}

/** R4: hint from shop markets - never hard-code “Try Canada or the UK”. */
export function previewCountryHint(
  marketCountries: string[] | undefined | null,
  selected?: string,
): string {
  const list = uniqCodes(marketCountries || []);
  const home = list[0];
  const other =
    list.find((c) => c !== home) ||
    (selected && selected !== home ? selected.toUpperCase() : null);
  if (home && other) {
    return `Your Markets first. Try ${countryLabel(other)} (not primary ${countryLabel(home)}). Developed / high-population countries are for Stay vs ask checks.`;
  }
  if (other) return `Try ${countryLabel(other)}.`;
  return "Pick a country that isn’t your primary market to see an ask.";
}

export function previewGroupLabel(group: PreviewCountryGroup): string {
  if (group === "market") return "Your Markets";
  if (group === "developed") return "Developed";
  return "High population";
}
